import CheckIcon from "@mui/icons-material/Check";
import Edit from "@mui/icons-material/Edit";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Buffer } from "buffer";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { apiPost, fetchData } from "../../common/services/ApiClient";
import {
  getCookiebyName,
  reduceObj,
  removeDuplicateArrayObj,
} from "../../common/services/Utilities";
import { ApiBaseUrl, AppUrl } from "../../config";
import FinancialReportingLevelOne from "../../containers/FinancialReportingLevelOne";
import PeerGroups from "../../containers/PeerGroups";
import useGetTableCheckboxState from "../../hooks/useGetTableCheckboxState";
import useSortProperties from "../../hooks/useSortProperties";
import {
  KeyBooleanValue,
  KeyValue,
  Query,
  SearchPillData,
  SortDirection,
} from "../../interfaces";
import { financialReportingSearchPills } from "../FinancialReporting/FinancialReportingLevelOne";
import ModalDialog from "../ModalDialog";
import MoreInfoButton from "../MoreInfoButton";
import SearchPills from "../SearchPills";
import PeerGroupCriteriaTable from "./PeerGroupCriteriaTable";
import peerGroupCustomCompareData from "./PeerGroupCustomCompareData";
import SaveNameInput from "./SaveNameInput";
import {
  standardComparisonOptions,
  subOptionLookup,
} from "./PeerGroupUtilities";
import { stringify, parse } from "flatted";
import { isEqual } from "lodash";

const useStyles = makeStyles(theme => ({
  addSelection: {
    "&.MuiButton-root.MuiButton-containedPrimary": {
      margin: "1rem 1rem 0 1rem",
      width: 300,
      height: 40,
    },

    "fontSize": "1rem",
  },
  resetSelection: {
    "&.MuiButton-root.MuiButton-containedPrimary": {
      margin: "1rem 1rem 0 1rem",
      height: 40,
      textAlign: "right",
    },
    "fontSize": "1rem",
  },
  pgcNameField: {
    width: 400,
    marginBottom: "1rem",
  },
  tableWrap: {
    "& th#cert-header-0": {
      whiteSpace: "nowrap",
    },
  },
  modalHeaderText: {
    paddingBottom: 0,
  },
  selectionBox: {
    "marginBottom": "2.5rem",
    "& h4": {
      paddingBottom: ".5rem",
    },
    "& .MuiCardContent-root": {
      padding: 24,
    },
    "& ul": {
      display: "inline-flex",
    },
    "& .MuiTypography-body2": {
      paddingBottom: 0,
    },
  },
  createPeerGroupSearch: {
    "&.MuiButton-root.MuiButton-containedPrimary": {
      height: 40,
      fontSize: "1rem",
      margin: ".5rem auto 2.5rem auto",
      display: "block",
    },
  },
  checkBox: {
    "color": theme.palette.primary.main,
    "&.MuiCheckbox-colorSecondary.Mui-checked": {
      color: theme.palette.primary.main,
    },
  },
  checkBoxTitle: {
    paddingBottom: 0,
  },
  variableErrors: {
    "& h3": { padding: 0 },
  },
  fieldErrors: {
    "border": `1px solid ${theme.palette.error.light}`,
    "wordBreak": "break-word",
    "padding": ".25rem",
    "borderRadius": 5,
    "& .fieldName p": {
      padding: ".5rem",
      textAlign: "left",
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
    "& .modifyButton": {
      "padding": ".5rem",
      "float": "right",
      "&:hover": { backgroundColor: "#ffffff" },
    },
  },
  editField: {
    "& .MuiOutlinedInput-adornedEnd": {
      paddingRight: 6,
    },
    "& .MuiIconButton-root": {
      padding: 8,
    },
    "& input": {
      padding: "16.5px 13px",
      fontSize: 14,
    },
  },
  MuiSvgIcon: {
    color: theme.palette.primary.main,
    fontSize: "10px",
  },
  tips: {
    fontSize: "1rem",
    paddingBottom: 0,
    paddingLeft: 16,
  },
  introText: {
    paddingTop: 40,
  },
  textBubble: {
    position: "relative",
    borderColor: "rgb(172, 200, 223, 1)",
    boxSizing: "border-box",
    borderWidth: "2px",
    borderStyle: "solid",
    background:
      "linear-gradient(180deg, rgba(217, 232, 246, 1) 0%, rgba(255, 255, 255, 1) 20%, rgba(255, 255, 255, 1) 83%, rgba(216, 231, 245, 1) 100%)",
    padding: "32px",
    borderRadius: "8px",
    paddingBottom: "10px",
  },
  stepOne: {
    "& > h2": {
      paddingBottom: ".5rem",
    },
    "& > .select-label": {
      fontWeight: 800,
      paddingBottom: ".5rem",
    },
  },
  button: {
    "width": "100%",
    "backgroundColor": "#ffffff",
    "border": "1px solid rgba(172, 200, 223, 1)",
    "marginTop": ".5rem",
    "marginBottom": ".5rem",
    "&.focus": {
      backgroundColor: "#ffffff",
      border: "1px solid rgba(172, 200, 223, 1)",
      color: theme.palette.primary.main,
    },
    "&:hover": {
      "backgroundColor": theme.palette.primary.main,
      "border": "1px solid #ced6e3",
      "color": "#ffffff",
      "&:active": {
        backgroundColor: theme.palette.primary.main,
        color: "#ffffff",
      },
    },
    "&:disabled": {
      color: "#92a8c7",
    },
  },
  buttons: {
    "minWidth": "100%",
    "backgroundColor": "#ffffff",
    "lineHeight": "1rem",
    "border": "1px solid rgba(172, 200, 223, 1)",
    "height": "50px",
    "color": theme.palette.primary.main,
    "&:focus": {
      backgroundColor: theme.palette.primary.main,
      color: "#ffffff",
    },
    "&:disabled": {
      backgroundColor: "#ffffff",
      color: "#92a8c7",
    },
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "#ffffff",
    },
  },
  dynamic: {
    "& div": {
      minWidth: 20,
      padding: 0,
    },
  },
}));

interface Props {
  appState: Query;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
  updateUi: (
    uiField: string,
    uiValue: KeyValue[] | string[] | boolean | string | KeyValue,
  ) => void;
  resetState: (appState: Query) => void;
  reportLimit?: number;
  buildSearch?: boolean;
  noCheckbox?: boolean;
  externalSaveVal?: boolean;
  externalSave?: (save: boolean) => void;
  noErrors?: (errors: boolean) => void;
  userId?: string;
}
export default function CustomizedTable({
  appState,
  updateSort,
  updateUi,
  reportLimit,
  buildSearch,
  noCheckbox,
  externalSaveVal,
  externalSave,
  noErrors,
  resetState,
  userId,
}: Props) {
  const user = getCookiebyName("id_token") || userId;
  const history = useHistory();
  const classes = useStyles();
  const [errors, setErrors] = React.useState<KeyBooleanValue>({
    noName: false,
    invalidName: false,
    noSearch: false,
    noCerts: false,
  });
  const hasErrors = Object.values(errors).includes(true);
  const [invalidCerts, setInvalidCerts] = React.useState<string[]>([]);

  const [editError, setEditError] = React.useState("");
  const [tempNewInvalidCerts, setTempNewInvalidCerts] = React.useState<
    string[]
  >(invalidCerts);
  const [clearCheckboxes, setClearCheckboxes] = React.useState(false);

  const [openCertModal, setOpenCertModal] = React.useState(false);

  const [openSearchModal, setOpenSearchModal] = React.useState(false);

  const [customPeerGroups, setCustomPeerGroups] = React.useState<
    SearchPillData[]
  >(appState.ui.customPeerGroups ?? []);

  const [individualCert, setIndividualCert] = React.useState<SearchPillData[]>(
    appState.ui.individualCert ?? [],
  );

  const [useAppState, setUseAppState] = React.useState(true);

  const [saveToCustomPG, setSaveToCustomPG] = React.useState(false);

  const [editId, setEditId] = React.useState("");
  const [editName, setEditName] = React.useState("");

  const financialSearchPillData =
    appState.filters.reportPeriod[0] &&
    financialReportingSearchPills(appState).filter(
      (searchPill: SearchPillData) => !searchPill.static,
    );
  const [financialSearchPills, setFinancialSearchPills] = React.useState<
    SearchPillData[]
  >(financialSearchPillData);

  const [
    standardGroupSearchPills,
    setStandardGroupSearchPills,
  ] = React.useState<SearchPillData[]>(
    appState.ui.standardGroupSearchPills ?? [],
  );

  const [peerGroupName, setPeerGroupName] = React.useState<string>("");

  const [newCheckState, setNewCheckState] = React.useState<KeyValue>({});

  const sortProperties = useSortProperties(appState, updateSort);

  const tableCheckBoxState: KeyValue = useGetTableCheckboxState(
    appState,
    clearCheckboxes,
    setClearCheckboxes,
    newCheckState,
  );
  const allCheckboxes = tableCheckBoxState.body.allCheckboxes;

  const [displayErrorMessage, setDisplayErrorMessage] = React.useState(false);

  const [initialLoad, setInitialLoad] = React.useState(true);
  React.useEffect(() => {
    if (appState.ui?.customPeerGroups) {
      setCustomPeerGroups(appState.ui.customPeerGroups);
    }
    if (appState.ui?.individualCert) {
      setIndividualCert(appState.ui.individualCert);
    }
    if (appState.ui?.standardGroupSearchPills) {
      setStandardGroupSearchPills(appState.ui.standardGroupSearchPills);
    }
  }, [
    appState.ui?.customPeerGroups,
    appState.ui?.individualCert,
    appState.ui?.standardGroupSearchPills,
  ]);

  const [savedGroups, setSavedGroups] = React.useState<KeyValue[]>([]);

  const [duplicateNameCheck, setDuplicateNameCheck] = React.useState<boolean>(
    false,
  );

  React.useEffect(() => {
    let isMounted = true;
    if (user) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/peer_groups/my_groups`,
          apiPost(
            { filters: "is_custom:true", limit: 150, offset: 0 },
            { Authorization: `Bearer ${user}` },
          ),
        )
          .then(response => {
            if (isMounted && user) {
              setSavedGroups(response.data);
            }
          })
          .catch(error => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [user]);

  React.useEffect(() => {
    const groupId = appState.ui.savedGroupId;
    if (groupId && initialLoad && getCookiebyName("uuid")) {
      setInitialLoad(false);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/peer_groups?filters=ID:"${groupId}"&user=${getCookiebyName(
            "uuid",
          )}`,
        )
          .then(response => {
            if (response.data && response.data.length > 0) {
              const group = response.data[0];
              const savedState = JSON.parse(
                Buffer.from(group.saved_state, "base64").toString("utf-8"),
              );
              const newAppState = {
                ...appState,
                filters: savedState.filters,
                search: savedState.search,
              };
              if (!appState.ui.savedPGCSearch) {
                setEditId(group.ID);
                setEditName(group.name);
                setPeerGroupName(group.name);
                if (group.isMultiCert) {
                  const certs = group.filter_criteria[0].value;
                  setManuallyAddedCerts(certs);
                } else if (
                  group.saved_state &&
                  JSON.stringify(appState) !== JSON.stringify(newAppState)
                ) {
                  resetState(newAppState);
                  updateUi("searchPush", true);
                }
              }
            }
          })
          .catch(error => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
    }
  }, [appState, initialLoad, resetState, updateUi]);

  React.useEffect(() => {
    if (Object.values(errors).includes(true) && externalSaveVal) {
      setDisplayErrorMessage(true);
    } else if (!Object.values(errors).includes(true) && displayErrorMessage) {
      setDisplayErrorMessage(false);
    }
  }, [displayErrorMessage, errors, externalSaveVal]);
  React.useEffect(() => {
    const certArray: string[] = [];
    appState.ui.peerGroups.map((cert: string) => {
      return certArray.push(cert);
    });
  }, [appState.ui.peerGroups]);

  const createPeerGroup = (data?: SearchPillData[]) => {
    const certList = Object.keys(allCheckboxes).filter((val: string) => {
      return allCheckboxes[val];
    });
    const peerGroupPill: SearchPillData = {
      value: peerGroupName,
      label: "Peer Group",
      static: false,
      search: false,
      formKey: data ? "search" : "cert",
      pillData: data ? fetchPillData : certList,
      reportPeriod: appState.filters.reportPeriod[0],
      id: Date.now().toString(),
    };
    setCustomPeerGroups([...customPeerGroups, peerGroupPill]);
    setClearCheckboxes(true);
    setOpenCertModal(false);
    setPeerGroupName("");
    setOpenSearchModal(false);

    saveToCustomPG &&
      (async () => {
        fetch(
          `${ApiBaseUrl()}/peer_groups`,
          apiPost(
            peerGroupCustomCompareData(
              [peerGroupName],
              [peerGroupPill],
              undefined,
              editId,
            )[0],
            { Authorization: `Bearer ${user}` },
          ),
        ).catch(error => console.error(error));
      })();
  };

  React.useEffect(() => {
    const duplicateName =
      peerGroupName !== editName &&
      savedGroups.map((group: any) => group.name).includes(peerGroupName);
    setDuplicateNameCheck(duplicateName);
  }, [savedGroups, peerGroupName, editName]);

  const invalidCharactersCheck = /[&+#,]/g.test(peerGroupName);
  const [priorSelections, setPriorSelections] = React.useState<
    SearchPillData[]
  >([]);

  React.useEffect(() => {
    const locallyStoredAppStateUi = localStorage.getItem("appStateUi");
    const appStateUi = appState.ui;
    const storedAppStateUi = locallyStoredAppStateUi
      ? parse(locallyStoredAppStateUi)
      : appStateUi;
    let updateLocalStorage = false;
    const storedSelections = {
      customPeerGroups: storedAppStateUi?.customPeerGroups || [],
      individualCert: storedAppStateUi?.individualCert || [],
      standardGroupSearchPills:
        storedAppStateUi?.standardGroupSearchPills || [],
    };
    if (
      customPeerGroups?.length &&
      !isEqual(customPeerGroups, storedAppStateUi?.customPeerGroups)
    ) {
      storedSelections.customPeerGroups = customPeerGroups;
      updateLocalStorage = true;
    }
    if (
      standardGroupSearchPills?.length &&
      !isEqual(
        standardGroupSearchPills,
        storedAppStateUi?.standardGroupSearchPills,
      )
    ) {
      storedSelections.standardGroupSearchPills = standardGroupSearchPills;
      updateLocalStorage = true;
    }
    if (
      individualCert?.length &&
      !isEqual(individualCert, storedAppStateUi?.individualCert)
    ) {
      storedSelections.individualCert = individualCert;
      updateLocalStorage = true;
    }
    //update the localstorage if the ui selections changed
    if (updateLocalStorage) {
      appStateUi.individualCert = storedSelections.individualCert;
      appStateUi.standardGroupSearchPills =
        storedSelections.standardGroupSearchPills;
      appStateUi.customPeerGroups = storedSelections.customPeerGroups;
      // CodeQL: js/clear-text-storage-of-sensitive-data: false positive
      localStorage.setItem("appStateUi", stringify(appStateUi));
    }
  }, [appState.ui, customPeerGroups, individualCert, standardGroupSearchPills]);

  React.useEffect(() => {
    if (useAppState) {
      const selections: SearchPillData[] = appState.ui.peerGroups.map(
        (cert: string) => {
          return {
            value: cert,
            label: "CERT",
            static: false,
            search: false,
            formKey: "cert",
          };
        },
      );
      return setPriorSelections(selections);
    }
  }, [allCheckboxes, appState.ui.peerGroups, useAppState]);

  const modalInputField = (
    <SaveNameInput
      width="large"
      value={peerGroupName}
      onChange={setPeerGroupName}
      header="Name This Peer Group"
      errors={
        duplicateNameCheck ||
        invalidCharactersCheck ||
        (errors.noName && displayErrorMessage)
      }
      errorText={
        duplicateNameCheck
          ? "Peer group name already exists"
          : invalidCharactersCheck
          ? "Peer group name cannot include the following characters: & + # ,"
          : errors.noName && displayErrorMessage
          ? `Name field is required when creating ${
              noCheckbox
                ? "a Dynamic Peer Group."
                : "an Explicit Cert Peer Group."
            }`
          : ""
      }
    />
  );
  const [manuallyAddedCerts, setManuallyAddedCerts] = React.useState<string[]>(
    [],
  );

  function editSubmit(currentError: string, newError: string) {
    //sets the editError to empty so the form exits without saving if the error is not updated, or if it's blank
    if (currentError === newError || !newError) {
      setEditError("");
      setTempNewInvalidCerts([]);
    } else {
      let editErrors: string[] = [];
      const newErrorState = invalidCerts.map((value: string) =>
        value === currentError ? newError : value,
      );

      newErrorState.forEach((field: string) => {
        if (!Object.keys(newCheckState).includes(field)) {
          editErrors.push(field);
        }
      });

      updateUi("addCertList", editErrors);
    }
  }

  React.useEffect(() => {
    let isMounted = true;

    if (appState.ui.addCertList && appState.ui.addCertList.length > 0) {
      const certs = appState.ui.addCertList.filter(
        (cert: string) => cert !== "",
      );
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/financials?agg_limit=10000&agg_by=CERT&filters=CERT:(${certs.join(
            " OR ",
          )})`,
        )
          .then(response => {
            if (isMounted) {
              updateUi("addCertList", []);
              const responseCerts = response.data.map((res: KeyValue) =>
                res.CERT.toString(),
              );

              //gets list of certs without duplicates
              const certList: string[] =
                tempNewInvalidCerts.length > 0
                  ? tempNewInvalidCerts
                  : Array.from(new Set(certs));

              const invalid = certList.filter(
                (cert: string) =>
                  [...manuallyAddedCerts, ...responseCerts].indexOf(cert) ===
                  -1,
              );
              const newCerts = certList.filter(
                (cert: string) => !invalid.includes(cert),
              );
              const unique: string[] = [];
              const notUnique: string[] = [];
              //maps through the cert list and pushes values to the string array needed for the table
              newCerts.forEach((val: string) => {
                if (Object.keys(allCheckboxes).includes(val)) {
                  notUnique.push(val);
                } else if (!invalidCerts.includes(val)) {
                  unique.push(val);
                }
              });

              if (notUnique.length > 0) {
                //if the cert exists in the table, it reduces it for the checkbox state
                const reducedNewState = reduceObj(
                  notUnique.map((val: string) => {
                    return { key: val, value: true };
                  }),
                );
                setNewCheckState({
                  ...allCheckboxes,
                  ...reducedNewState,
                });
              }
              if (unique.length > 0) {
                setManuallyAddedCerts(
                  Array.from(new Set([...manuallyAddedCerts, ...unique])),
                );
              }

              setInvalidCerts(
                tempNewInvalidCerts.length > 0
                  ? invalid
                  : Array.from(new Set([...invalidCerts, ...invalid])),
              );
              setTempNewInvalidCerts([]);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  });

  const selectSearchPills: SearchPillData[] = Object.keys(allCheckboxes)
    .filter(val => allCheckboxes[val])
    .map((pill: string) => {
      return {
        value: pill,
        label: "CERT",
        static: false,
        search: false,
        formKey: "cert",
        reportPeriod: appState.filters.reportPeriod[0],
      };
    });

  const explicitCerts = [
    ...manuallyAddedCerts,
    ...Object.keys(allCheckboxes).filter(val => allCheckboxes[val]),
  ].map((pill: string) => {
    return {
      value: pill,
      label: "CERT",
      static: false,
      search: false,
      formKey: "cert",
      reportPeriod: appState.filters.reportPeriod[0],
    };
  });

  const customCertSearchPills = (
    <SearchPills
      dataArray={selectSearchPills}
      deleteSearchPill={(e: SearchPillData) =>
        setNewCheckState({
          ...allCheckboxes,
          [e.value]: false,
        })
      }
    />
  );

  React.useEffect(() => {
    const matchingCerts = Object.keys(allCheckboxes).filter((element: string) =>
      manuallyAddedCerts.includes(element),
    );
    if (matchingCerts.length > 0) {
      const newCertArray = manuallyAddedCerts.filter(
        (value: string) => matchingCerts.indexOf(value) === -1,
      );
      const newCheckedCertArray = reduceObj(
        matchingCerts.map((cert: string) => {
          return { key: cert, value: true };
        }),
      );

      setManuallyAddedCerts(newCertArray);
      setNewCheckState({ ...allCheckboxes, ...newCheckedCertArray });
    }
  }, [allCheckboxes, manuallyAddedCerts]);

  const explicitCertPills = (
    <SearchPills
      dataArray={explicitCerts}
      deleteSearchPill={(e: SearchPillData) => {
        if (Object.keys(allCheckboxes).includes(e.value)) {
          setNewCheckState({
            ...allCheckboxes,
            [e.value]: false,
          });
        } else if (manuallyAddedCerts.includes(e.value)) {
          setManuallyAddedCerts(
            manuallyAddedCerts.filter((val: string) => e.value !== val),
          );
        }
      }}
    />
  );

  const currentSearchCriteria = (
    <SearchPills
      dataArray={financialSearchPills}
      deleteSearchPill={(e: SearchPillData) => {
        const newFinancialSearchPills = financialSearchPills.filter(
          (pill: SearchPillData) => {
            return JSON.stringify(pill) !== JSON.stringify(e);
          },
        );
        setFinancialSearchPills(newFinancialSearchPills);
      }}
    />
  );

  const saveToCustomCheckBox = getCookiebyName("uuid") && (
    <FormGroup>
      <FormControlLabel
        control={
          <Checkbox
            id="save_to_custom"
            className={classes.checkBox}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSaveToCustomPG(e.target.checked)
            }
          />
        }
        label={
          <Typography
            variant="body2"
            color="primary"
            className={classes.checkBoxTitle}
          >
            Save to my Custom Peer Groups List
          </Typography>
        }
      />
    </FormGroup>
  );
  const searchCriteria =
    financialSearchPills.length > 0
      ? financialSearchPills.filter(data => typeof data.value)
      : [];
  const array = ["name", "bhcName", "bhcRSSD", "multiCert", "cert"];
  const createDynamic = searchCriteria.some(
    (item: KeyValue) =>
      !array.includes(item.formKey) &&
      item.value !== "" &&
      item.value !== undefined,
  );
  const peerGroupCertModal = (
    <ModalDialog
      title={"Static Peer Group Creation"}
      display={openCertModal}
      setDisplay={setOpenCertModal}
      maxWidth="md"
      noCloseXIcon
      onExited={() => setPeerGroupName("")}
      onSubmit={createPeerGroup}
      submitText="Create Peer Group"
      disabledSubmit={
        !peerGroupName ||
        Object.values(allCheckboxes).every(val => val !== true) ||
        duplicateNameCheck ||
        invalidCharactersCheck
      }
    >
      {modalInputField}
      <Typography
        variant="body2"
        color="primary"
        className={classes.modalHeaderText}
      >
        <b>Static Definition Method</b>
      </Typography>
      <Typography variant="body2" color="primary">
        You have selected a loose collection of Certs that can only be combined
        as a Static Peer Group. Due to their nature, Static Peer Groups can lose
        individual reporting data between reporting periods.
      </Typography>
      {customCertSearchPills}
      {saveToCustomCheckBox}
    </ModalDialog>
  );
  const [openCertPreview, setOpenCertPreview] = React.useState(false);
  const [certPreviewState, setCertPreviewState] = React.useState({});
  const previewGroup = () => {
    setOpenCertPreview(true);
    setCertPreviewState({
      filters: [
        {
          variable: "CERT",
          value: explicitCerts.map((cert: KeyValue) => cert.value),
          operator: "=",
        },
      ],
      title: "Preview Group",
      isMulti: true,
      custom: true,
      appState: {
        filters: {
          reportPeriod: appState.filters.reportPeriod,
          financials: "",
          multiCert: explicitCerts.map((cert: KeyValue) => cert.value),
        },
        ui: {},
        search: {},
      },
    });
  };
  const peerGroupSearchModal = (
    <ModalDialog
      title="Dynamic Peer Group Creation"
      display={openSearchModal}
      onExited={() => setPeerGroupName("")}
      setDisplay={setOpenSearchModal}
      maxWidth="md"
      onSubmit={() => {
        createPeerGroup(financialSearchPills.filter(data => data.value));
      }}
      submitText="Create Peer Group"
      disabledSubmit={
        !peerGroupName ||
        duplicateNameCheck ||
        invalidCharactersCheck ||
        (financialSearchPills &&
          financialSearchPills.filter(data => data.value).length === 0)
      }
    >
      {modalInputField}
      <Typography
        variant="body2"
        color="primary"
        className={classes.modalHeaderText}
      >
        <b>Dynamic Definition Method</b>
      </Typography>
      <Typography variant="body2" color="primary">
        You have selected to use Search Criteria to define this Peer Group.
        Dynamic Peer Groups are flexible and will use all institutions that
        match the criteria for the given reporting period.
      </Typography>
      {currentSearchCriteria}
      {saveToCustomCheckBox}
    </ModalDialog>
  );

  function addSelections(selections: SearchPillData[]) {
    const allCerts = [...individualCert, ...selections];
    const uniqueCerts = allCerts.filter(
      (obj, index, self) =>
        index === self.findIndex(cert => cert.value === obj.value),
    );
    setClearCheckboxes(true);
    setIndividualCert(uniqueCerts);
  }

  const regexTest = (e: React.ChangeEvent<HTMLInputElement>) =>
    RegExp(/^\d*$/).test(e.target.value);
  const handleEditErrorChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    error: string,
  ) => {
    if (regexTest(event)) {
      const newErrorValues = invalidCerts.map((field: string) =>
        field === error ? event.target.value : field,
      );
      setTempNewInvalidCerts(newErrorValues);
    }
  };

  //resets the newState for the delete
  React.useEffect(() => {
    if (Object.keys(newCheckState).length > 0) {
      setNewCheckState({});
    }
  }, [newCheckState]);

  React.useEffect(() => {
    if (
      !openSearchModal &&
      JSON.stringify(financialSearchPillData) !==
        JSON.stringify(financialSearchPills)
    ) {
      const timer = setTimeout(() => {
        setFinancialSearchPills(financialSearchPillData);
      }, 500);
      return () => clearTimeout(timer);
    }
  });

  React.useEffect(() => {
    if (externalSaveVal && noErrors) {
      if (hasErrors && externalSave) {
        noErrors(hasErrors);
        externalSave(false);
      } else {
        noErrors(hasErrors);
      }
    }
  }, [externalSave, externalSaveVal, hasErrors, noErrors]);

  const fetchPillData =
    financialSearchPills &&
    financialSearchPills
      .filter(data => data.value)
      .map((val: KeyValue) => {
        return {
          ...val,
          appState: appState,
        };
      });
  React.useEffect(() => {
    if (
      externalSaveVal &&
      externalSave &&
      !hasErrors &&
      getCookiebyName("uuid")
    ) {
      externalSave(false);
      setSaveToCustomPG(true);
      const certList = [
        ...Object.keys(allCheckboxes).filter((val: string) => {
          return allCheckboxes[val];
        }),
        ...manuallyAddedCerts,
      ];
      const peerGroupPill: SearchPillData = {
        value: peerGroupName,
        label: "Peer Group",
        static: false,
        search: false,
        formKey: noCheckbox ? "search" : "cert",
        pillData: noCheckbox ? fetchPillData : certList,
        reportPeriod: appState.filters.reportPeriod[0],
      };
      (async () => {
        fetch(
          `${ApiBaseUrl()}/peer_groups`,
          apiPost(
            peerGroupCustomCompareData(
              [peerGroupName],
              [peerGroupPill],
              undefined,
              editId,
            )[0],
            { Authorization: `Bearer ${user}` },
          ),
        ).catch(error => console.error(error));
      })();
    }
  }, [
    allCheckboxes,
    appState.filters.reportPeriod,
    editId,
    externalSave,
    externalSaveVal,
    fetchPillData,
    hasErrors,
    manuallyAddedCerts,
    noCheckbox,
    peerGroupName,
    user,
  ]);

  React.useEffect(() => {
    if (!peerGroupName && !errors.noName) {
      setErrors({ ...errors, noName: true });
    } else if (peerGroupName && errors.noName) {
      setErrors({ ...errors, noName: false });
    }
    if (invalidCharactersCheck && !errors.invalidName) {
      setErrors({ ...errors, invalidName: true });
    } else if (duplicateNameCheck && !errors.invalidName) {
      setErrors({ ...errors, invalidName: true });
    } else if (
      !invalidCharactersCheck &&
      !duplicateNameCheck &&
      errors.invalidName
    ) {
      setErrors({ ...errors, invalidName: false });
    }

    if (
      noCheckbox &&
      financialSearchPills &&
      financialSearchPills.filter(data => data.value).length === 0 &&
      !errors.noSearch
    ) {
      setErrors({ ...errors, noSearch: true });
    } else if (
      noCheckbox &&
      financialSearchPills &&
      financialSearchPills.filter(data => data.value).length !== 0 &&
      errors.noSearch
    ) {
      setErrors({ ...errors, noSearch: false });
    }
    if (!noCheckbox && explicitCerts.length === 0 && !errors.noCerts) {
      setErrors({ ...errors, noCerts: true });
    } else if (!noCheckbox && explicitCerts.length !== 0 && errors.noCerts) {
      setErrors({ ...errors, noCerts: false });
    }
  }, [
    errors,
    financialSearchPills,
    invalidCharactersCheck,
    explicitCerts.length,
    manuallyAddedCerts,
    noCheckbox,
    peerGroupName,
    selectSearchPills,
    selectSearchPills.length,
    duplicateNameCheck,
  ]);
  const [
    allSavedCustomPeerGroups,
    setAllSavedCustomPeerGroups,
  ] = React.useState<KeyValue[]>([]);

  const [
    allSavedStandardPeerGroups,
    setAllSavedStandardPeerGroups,
  ] = React.useState<KeyValue[]>([]);

  React.useEffect(() => {
    let isMounted = true;
    const user = getCookiebyName("uuid") || userId;
    const userCredentials = user ? `&user=${user}` : "";
    if (isMounted) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/peer_groups?limit=150${userCredentials}`,
        )
          .then(response => {
            const customGroups = response.data.filter(
              (data: KeyValue) => data.is_custom === true,
            );
            setAllSavedCustomPeerGroups(customGroups);
            const standardGroups = response.data.filter(
              (data: KeyValue) => data.is_custom === false,
            );
            setAllSavedStandardPeerGroups(standardGroups);
          })
          .catch(error => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [user, userId]);

  React.useEffect(() => {
    if (appState.ui.customPeerGroups) {
      const array = appState.ui.customPeerGroups;
      const newValue = array.length > 0 ? array[array.length - 1] : null;
      const exists = customPeerGroups.some(item => item.value === newValue);
      if (!exists && newValue && typeof newValue !== "object") {
        const val = allSavedCustomPeerGroups.find(
          peerGroup => peerGroup.name === newValue,
        );
        if (val) {
          const peerGroupPill: SearchPillData = {
            value: newValue,
            label: "PG",
            static: false,
            search: false,
            formKey: "search",
            pillData: [
              {
                appState: JSON.parse(
                  Buffer.from(val.saved_state, "base64").toString("utf-8"),
                ),
              },
            ],
            reportPeriod: appState.filters.reportPeriod[0],
          };
          setCustomPeerGroups([...customPeerGroups, peerGroupPill]);
        }
      }
    }
  }, [
    allSavedCustomPeerGroups,
    appState.filters.reportPeriod,
    appState.ui.customPeerGroups,
    customPeerGroups,
    fetchPillData,
  ]);

  const checkBoxProps = noCheckbox
    ? undefined
    : {
        header: tableCheckBoxState.header,
        body: tableCheckBoxState.body,
        key: "CERT",
      };

  const previewGroupModal = (
    <ModalDialog
      title={"Preview Group"}
      display={openCertPreview}
      setDisplay={setOpenCertPreview}
      maxWidth="lg"
    >
      <PeerGroupCriteriaTable
        currentGroup={certPreviewState}
        includeReportPeriod
        appState={appState}
      />
    </ModalDialog>
  );

  const IsoscelesTriangle = (
    width: number,
    height: number,
    facing?: string,
  ) => {
    const triangleStyle = {
      width: 0,
      height: 0,
      borderTop: `${height}px solid transparent`,
      borderBottom: `${height}px solid transparent`,
      ...(facing !== "left"
        ? { borderRight: `${width}px solid rgb(177,199,221)` }
        : { borderLeft: `${width}px solid rgb(177,199,221)` }),
    };

    return <div style={triangleStyle}></div>;
  };
  const theme = useTheme();
  const isBigViewport = useMediaQuery(theme.breakpoints.up("md"));
  const textBubbleSearch1 = (
    <div style={{ display: "flex", alignItems: "center" }}>
      {isBigViewport && IsoscelesTriangle(40, 68)}
      <Box
        display="flex"
        alignItems="center"
        style={{
          borderTopLeftRadius: isBigViewport ? "0px" : "8px",
          borderBottomLeftRadius: isBigViewport ? "0px" : "8px",
          width: "100%",
        }}
        className={classes.textBubble}
      >
        <Typography
          variant="body2"
          color="primary"
          style={{ fontSize: "16px" }}
        >
          Use the Search Panel on the left to find institutions to add to your
          report. Look them up by their certs, name, or by other defining
          criteria that Institutions can share. All fields are optional. Make
          your selections from the results table.
        </Typography>
      </Box>
    </div>
  );

  const [peerGroupSelections, setPeerGroupSelections] = React.useState(false);
  const [previousSelectedGroups, setPreviousSelectedGroups] = React.useState(
    {},
  );
  const subOptionsSelected =
    appState.ui?.subOptionsSelected &&
    Object.keys(appState.ui?.subOptionsSelected)?.length;

  React.useEffect(() => {
    if (
      JSON.stringify(previousSelectedGroups) !==
        appState.ui.subOptionsSelected &&
      subOptionsSelected
    ) {
      setPreviousSelectedGroups(appState.ui.subOptionsSelected);

      setPeerGroupSelections(
        Object.values(appState.ui.subOptionsSelected).some(
          value => value === true,
        ),
      );
    }
  }, [
    appState.ui.subOptionsSelected,
    previousSelectedGroups,
    subOptionsSelected,
  ]);

  const standardPeerGroupList: string[] = standardComparisonOptions.map(
    option => option.value,
  );

  const standardPeerGroupCustomCompare =
    appState.ui?.savedGroupId &&
    standardPeerGroupList.includes(appState.ui.savedGroupId);

  const [
    currentSubOptionsSelected,
    setCurrentSubOptionsSelected,
  ] = React.useState(appState.ui.subOptionsSelected);

  React.useEffect(() => {
    const standardPeerGroups = appState.ui.standardPeerGroups;
    let newPeerGroup = "";
    let willUpdate = false;
    newPeerGroup =
      standardPeerGroups?.length > 0
        ? standardPeerGroups[standardPeerGroups.length - 1]
        : null;
    if (
      subOptionsSelected &&
      !isEqual(currentSubOptionsSelected, appState.ui.subOptionsSelected)
    ) {
      willUpdate = true;
    }
    const searchPills: (string | undefined)[] = standardGroupSearchPills.map(
      item => item.short_name,
    );
    let searchPillAlreadyExists = false;
    if (searchPills?.includes(newPeerGroup)) {
      searchPillAlreadyExists = true;
    }
    if (
      !searchPillAlreadyExists &&
      newPeerGroup &&
      standardPeerGroupCustomCompare &&
      allSavedStandardPeerGroups?.length
    ) {
      let standardPeerGroupSelections: KeyValue[] = [];
      if (willUpdate || standardPeerGroups.length > 1) {
        setCurrentSubOptionsSelected(appState.ui.subOptionsSelected);
        standardPeerGroupSelections = allSavedStandardPeerGroups.filter(
          (item: KeyValue) =>
            standardPeerGroups.includes(item.short_name) &&
            !searchPills?.includes(item.short_name),
        );
      } else {
        standardPeerGroupSelections = allSavedStandardPeerGroups.filter(
          (item: KeyValue) => item.short_name === newPeerGroup,
        );
      }
      let searchPillSelections: SearchPillData[] = [];
      let selectionNames: string[] = [];
      standardPeerGroupSelections.forEach((val: KeyValue) => {
        const newData = {
          value: val.name,
          label: "PG",
          static: false,
          search: false,
          formKey: "search",
          id: val.ID,
          short_name: val.short_name,
          filter_criteria: val.filter_criteria,
          reportPeriod: appState.filters.reportPeriod[0],
        };
        selectionNames = [...selectionNames, val.short_name];
        searchPillSelections = [...searchPillSelections, newData];
      });
      setStandardGroupSearchPills([
        ...standardGroupSearchPills,
        ...searchPillSelections,
      ]);
    }
  }, [
    allSavedStandardPeerGroups,
    appState,
    currentSubOptionsSelected,
    standardGroupSearchPills,
    standardPeerGroupCustomCompare,
    subOptionsSelected,
  ]);

  let filteredCustomPeerGroups: KeyValue[] = [];
  if (
    appState.ui?.savedPGCSearch &&
    peerGroupSelections &&
    allSavedCustomPeerGroups.length > 0 &&
    subOptionsSelected
  ) {
    filteredCustomPeerGroups = allSavedCustomPeerGroups.filter(
      (item: KeyValue) => appState.ui.subOptionsSelected[item.ID] === true,
    );
  }

  const tableDisplay = (
    <>
      {appState.ui.searchPush ? (
        <Box className={classes.tableWrap}>
          {appState.ui.savedPGCSearch ? (
            <Grid container spacing={2}>
              <Grid item lg={12} md={12}>
                <PeerGroups
                  data={
                    standardPeerGroupCustomCompare
                      ? undefined
                      : filteredCustomPeerGroups
                  }
                  customCompare
                  groups={
                    standardPeerGroupCustomCompare
                      ? [appState.ui.savedGroupId]
                      : appState.ui.peerGroups
                  }
                />
              </Grid>
            </Grid>
          ) : (
            <div style={{ marginTop: 20 }}>
              <FinancialReportingLevelOne
                sortProperties={sortProperties}
                tableCheckboxProps={checkBoxProps}
                disableTableClick={buildSearch}
              />
            </div>
          )}
        </Box>
      ) : (
        <Box
          textAlign="center"
          paddingTop="2rem"
          maxWidth={575}
          margin="0 auto 2rem auto"
        >
          {noCheckbox && (
            <>
              <Typography variant="body2" color="primary">
                Dynamic peer groups utilize saved search criteria to find the
                peer group’s populations.
              </Typography>
              <Typography variant="body2" color="primary">
                Perform a search now to target the population you’re interested
                in.
              </Typography>
            </>
          )}
        </Box>
      )}
      {buildSearch && !noCheckbox && (
        <>
          <Box className={classes.selectionBox}>
            <Typography variant="h4" color="primary">
              Selections to be added to Peer Group
            </Typography>
            <Card color="primary" id="shopping-cart1">
              <CardContent>
                <Box textAlign={"center"}>
                  {explicitCerts.length === 0 ? (
                    <Typography component="div" color="primary" variant="body1">
                      {errors.noCerts && buildSearch && displayErrorMessage && (
                        <Alert
                          id="certs-required"
                          severity="error"
                          style={{ padding: "0 .5rem", marginBottom: "1rem" }}
                        >
                          Certs required when creating an Explicit Cert Peer
                          Group.
                        </Alert>
                      )}
                      Search for institutions, then use this area to compile
                      your peer group Cert list.
                    </Typography>
                  ) : (
                    <Grid container>
                      <Grid item xs={12}>
                        {explicitCertPills}
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          color="primary"
                          disableElevation
                          onClick={() => {
                            setManuallyAddedCerts([]);
                            setNewCheckState(
                              reduceObj(
                                Object.keys(allCheckboxes).map(
                                  (checkbox: string) => {
                                    return { key: checkbox, value: false };
                                  },
                                ),
                              ),
                            );
                          }}
                          className={classes.addSelection}
                        >
                          Clear Selections
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          disableElevation
                          id="previewPoulation"
                          className={classes.addSelection}
                          onClick={previewGroup}
                        >
                          Preview Population
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
          {invalidCerts.length > 0 && (
            <Box className={classes.selectionBox}>
              <Typography variant="h4" color="error">
                The following certs do not exist and will not be saved. Edit
                them here to correct errors, or continue without them.
              </Typography>
              <Card color="primary" id="error-variables">
                <CardContent>
                  <Box textAlign={"center"}>
                    <Typography color="primary" variant="body2"></Typography>

                    <Grid container>
                      <Grid item xs={12}>
                        <Box
                          id="variableErrors"
                          className={classes.variableErrors}
                        >
                          <Grid container spacing={2}>
                            {invalidCerts.map(
                              (error: string, index: number) => {
                                return (
                                  <Grid item xs={3} key={index}>
                                    {editError === error ? (
                                      <form
                                        key={index}
                                        onSubmit={(e: React.FormEvent) => {
                                          e.preventDefault();

                                          editSubmit(
                                            error,
                                            tempNewInvalidCerts[index],
                                          );
                                        }}
                                      >
                                        <FormControl fullWidth>
                                          <TextField
                                            onInvalid={(
                                              e: React.ChangeEvent<
                                                HTMLInputElement
                                              >,
                                            ) => {
                                              if (regexTest(e)) {
                                                return e.target.setCustomValidity(
                                                  "Only numeric values allowed.",
                                                );
                                              } else {
                                                return e.target.setCustomValidity(
                                                  "",
                                                );
                                              }
                                            }}
                                            className={classes.editField}
                                            fullWidth
                                            id="modifyTextField"
                                            variant="outlined"
                                            inputProps={{
                                              pattern: "^[0-9]*$",
                                              title:
                                                "Input must only contain numbers",
                                            }}
                                            onChange={(
                                              e: React.ChangeEvent<
                                                HTMLInputElement
                                              >,
                                            ) =>
                                              handleEditErrorChange(e, error)
                                            }
                                            defaultValue={error}
                                            InputProps={{
                                              endAdornment: (
                                                <InputAdornment position="end">
                                                  <IconButton
                                                    id="saveEdit"
                                                    type="submit"
                                                    title="Save Edit"
                                                    size="large"
                                                  >
                                                    <CheckIcon color="primary" />
                                                  </IconButton>
                                                </InputAdornment>
                                              ),
                                            }}
                                          />
                                        </FormControl>
                                      </form>
                                    ) : (
                                      <Box key={index}>
                                        <Grid
                                          container
                                          id={`error-${error}`}
                                          className={classes.fieldErrors}
                                        >
                                          <Grid
                                            item
                                            xs={9}
                                            className="fieldName"
                                          >
                                            <Typography color="error">
                                              {error}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={3}>
                                            <IconButton
                                              title="Edit Item"
                                              id={`edit-${error}`}
                                              className="modifyButton"
                                              onClick={() =>
                                                setEditError(error)
                                              }
                                              size="large"
                                            >
                                              <Edit color="primary" />
                                            </IconButton>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    )}
                                  </Grid>
                                );
                              },
                            )}
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </>
      )}
    </>
  );
  const items = [
    "Create a Dynamic Peer Group using the shared characteristics of your search criteria",
    "Create a Static Peer Group (a set list of certs)",
    "Or as separate/individual Institutions",
  ];
  const textBubbleSearch2 = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        paddingLeft: isBigViewport ? "42px" : "0px",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        style={{
          borderTopRightRadius: isBigViewport ? "0px" : "8px",
          borderBottomRightRadius: isBigViewport ? "0px" : "8px",
          width: "100%",
        }}
        className={classes.textBubble}
      >
        <Typography
          variant="body2"
          color="primary"
          style={{ fontSize: "16px" }}
        >
          Then, use the Selections Manager interface on the right to indicate
          how you want your selected institutions added to your report. You can
          choose from the following options:
          <ul>
            {items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </Typography>
      </Box>
      {isBigViewport && IsoscelesTriangle(40, 107, "left")}
    </div>
  );

  const textBubbleSavedPeerGroup = (
    <div style={{ display: "flex", alignItems: "center" }}>
      {isBigViewport && IsoscelesTriangle(40, 100)}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        style={{
          borderTopLeftRadius: isBigViewport ? "0px" : "8px",
          borderBottomLeftRadius: isBigViewport ? "0px" : "8px",
          borderTopRightRadius: isBigViewport ? "0px" : "8px",
          borderBottomRightRadius: isBigViewport ? "0px" : "8px",
          width: "100%",
        }}
        className={classes.textBubble}
      >
        <Typography
          variant="body2"
          color="primary"
          style={{ fontSize: "16px" }}
        >
          Use the Search Panel on the left to select from the standard peer
          groups and/or your own saved custom peer groups for your report. You
          can preview each peer group to ensure they are correct for your
          purposes.
        </Typography>
        <Typography
          variant="body2"
          color="primary"
          style={{ fontSize: "16px" }}
        >
          Add peer groups singly, using each card’s interface, or add an entire
          range of sub-peer groups using the button that will appear on the
          right.
        </Typography>
      </Box>
      {isBigViewport && IsoscelesTriangle(40, 100, "left")}
    </div>
  );

  const textBubbleToShow = !appState.ui.savedPGCSearch ? (
    <>
      {textBubbleSearch1}
      <br />
      {textBubbleSearch2}
    </>
  ) : (
    <>{textBubbleSavedPeerGroup}</>
  );
  const addAllPeerGroupsToReport = () => {
    let searchPillSelections: SearchPillData[] = [];
    let selectionNames: string[] = [];
    if (standardPeerGroupCustomCompare && appState.ui.savedPGCSearch) {
      const name: string = appState.ui.savedGroupId;
      const standardGroups = appState.ui.standardPeerGroups;
      if (subOptionsSelected) {
        const selectedKeys = Object.keys(appState.ui.subOptionsSelected).filter(
          key => appState.ui.subOptionsSelected[key],
        );
        const selectedLookupKeys = Object.entries(subOptionLookup)
          .filter(([key, value]) => selectedKeys.includes(value))
          .map(([key]) => key);
        const arr = [...standardGroups, ...selectedLookupKeys];
        const uniqueArray = (arr: string[]) =>
          arr.filter((value, index, self) => self.indexOf(value) === index);
        updateUi("standardPeerGroups", uniqueArray(arr));
      } else {
        updateUi("standardPeerGroups", [
          ...(standardGroups?.length ? standardGroups : []),
          name,
        ]);
      }
    } else {
      const allSelections = allSavedCustomPeerGroups.filter(
        (item: KeyValue) => appState.ui.subOptionsSelected[item.ID] === true,
      );
      allSelections.forEach((val: KeyValue) => {
        const newData = {
          value: val.name,
          label: "PG",
          static: false,
          search: false,
          formKey: "search",
          id: val.ID,
          pillData: [
            {
              appState: JSON.parse(
                Buffer.from(val.saved_state, "base64").toString("utf-8"),
              ),
            },
          ],
          reportPeriod: appState.filters.reportPeriod[0],
        };
        selectionNames = [...selectionNames, val.name];
        searchPillSelections = [...searchPillSelections, newData];
      });
      const combinedData = removeDuplicateArrayObj(
        [...searchPillSelections, ...customPeerGroups],
        "id",
      ) as SearchPillData[];
      setCustomPeerGroups(combinedData);
      updateUi("customPeerGroups", combinedData);
    }
  };

  const reportPopulationContent = (
    <Grid container spacing={2} mt={2}>
      <Grid item lg={8.6} md={12}>
        <Typography
          color="primary"
          variant="body1"
          component="div"
          id={"report-population"}
        >
          {customPeerGroups?.length > 0 ||
          standardGroupSearchPills?.length > 0 ||
          individualCert?.length > 0 ? (
            <>
              <Grid item xs={12}>
                <SearchPills
                  dataArray={individualCert}
                  deleteSearchPill={(e: SearchPillData) => {
                    const newPills = priorSelections.filter(
                      element => element.value !== e.value,
                    );
                    setUseAppState(false);
                    setPriorSelections(newPills);
                    setNewCheckState({
                      ...allCheckboxes,
                      [e.value]: false,
                    });
                    const newIndividualCerts = individualCert.filter(
                      (groups: SearchPillData) => {
                        return groups.value !== e.value;
                      },
                    );
                    setIndividualCert(newIndividualCerts);
                  }}
                />
                <SearchPills
                  secondaryColor
                  dataArray={customPeerGroups}
                  deleteSearchPill={(e: SearchPillData) => {
                    const newPeerGroups = customPeerGroups.filter(
                      (groups: SearchPillData) => {
                        return groups.value !== e.value;
                      },
                    );
                    setCustomPeerGroups(newPeerGroups);
                    updateUi(
                      "customPeerGroups",
                      newPeerGroups
                        .filter(item => item.value !== null)
                        .map(item => item.value),
                    );
                  }}
                />
                <SearchPills
                  standardColor
                  dataArray={standardGroupSearchPills}
                  deleteSearchPill={(e: SearchPillData) => {
                    const newSearchPills = standardGroupSearchPills.filter(
                      (groups: SearchPillData) => {
                        return groups.value !== e.value;
                      },
                    );
                    const newStandardPeerGroups = appState.ui.standardPeerGroups.filter(
                      (groups: string) => {
                        return groups !== e.short_name;
                      },
                    );
                    setStandardGroupSearchPills(newSearchPills);
                    updateUi("standardPeerGroups", newStandardPeerGroups);
                    updateUi("standardGroupSearchPills", newSearchPills);
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                {customPeerGroups?.length +
                  individualCert?.length +
                  standardGroupSearchPills?.length >
                  (reportLimit ? reportLimit : 100) && (
                  <Alert id="limitError" severity="error">
                    You have exceeded the limit of 100 individual columns to be
                    added to the report. Please reduce the number of columns
                    selected.
                  </Alert>
                )}
              </Grid>
            </>
          ) : (
            <Typography
              id="selectionsPlaceholder"
              variant="body2"
              color="primary"
              style={{
                fontSize: "13px",
                width: 350,
              }}
            >
              Your selected institutions and prepared peer groups will be held
              here until you are ready for the next step of building your
              report.
            </Typography>
          )}
        </Typography>
      </Grid>
      <Grid item lg={3.4} md={12}>
        <Button
          id="continueReport"
          fullWidth
          color="secondary"
          variant="contained"
          disabled={
            customPeerGroups?.length +
              individualCert?.length +
              standardGroupSearchPills?.length >
              (reportLimit ?? 100) ||
            customPeerGroups?.length +
              individualCert?.length +
              standardGroupSearchPills?.length ===
              0
          }
          disableElevation
          className={classes.addSelection}
          onClick={() => {
            updateUi("customPeerGroups", customPeerGroups);
            updateUi("standardGroupSearchPills", standardGroupSearchPills);
            updateUi("individualCert", individualCert);
            updateUi("pgcStep", "step2");
            const peerGroups = [
              ...individualCert,
              ...customPeerGroups,
              ...standardGroupSearchPills,
            ].map((cert: KeyValue) =>
              cert?.short_name && !cert.is_custom
                ? cert.short_name
                : cert.value,
            );
            const appStateUi = appState.ui;
            appStateUi.pgcStep = "step2";
            appStateUi.peerGroups = peerGroups;
            // CodeQL: js/clear-text-storage-of-sensitive-data: false positive
            localStorage.setItem("appStateUi", stringify(appStateUi));
            history.push({
              pathname: `${AppUrl("PeerGroup")}/customized`,
              search: `peerGroups=${peerGroups}`,
            });
          }}
        >
          Continue to Next Step
        </Button>
        <Grid container justifyContent="flex-end">
          <Grid item xs={6}>
            <Button
              id="resetSelections"
              color="secondary"
              variant="contained"
              fullWidth
              className={classes.resetSelection}
              disabled={
                customPeerGroups?.length +
                  individualCert?.length +
                  standardGroupSearchPills?.length >
                  (reportLimit ?? 100) ||
                customPeerGroups?.length +
                  individualCert?.length +
                  standardGroupSearchPills?.length ===
                  0
              }
              disableElevation
              onClick={() => {
                updateUi("customPeerGroups", []);
                updateUi("standardGroupSearchPills", []);
                updateUi("standardPeerGroups", []);
                updateUi("subOptionsSelected", {});
                updateUi("individualCert", []);
                setStandardGroupSearchPills([]);
                setIndividualCert([]);
                setCustomPeerGroups([]);
                localStorage.removeItem("appStateUi");
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item lg={12} md={12}>
        {appState.ui.savedPGCSearch &&
          (peerGroupSelections || standardPeerGroupCustomCompare) &&
          appState.ui.searchPush && (
            <>
              <br />
              <Typography variant="h2">Peer Group Review</Typography>
              <hr />
            </>
          )}
      </Grid>
      <Grid item lg={8.6} md={12}>
        {!appState.ui.searchPush ||
        (appState.ui.savedPGCSearch &&
          !peerGroupSelections &&
          !standardPeerGroupCustomCompare)
          ? textBubbleToShow
          : tableDisplay}
      </Grid>
      <Grid item lg={3.4} md={12}>
        {!appState.ui.savedPGCSearch ? (
          <div>
            <Typography variant="h3">Selections Manager</Typography>

            <Box
              sx={{
                display: "block",
                backgroundColor: "#d9e7f7",
                border: "1px solid #acc8df",
                borderRadius: "5px",
                padding: "1rem",
              }}
            >
              <Typography style={{ paddingBottom: 8 }} variant="h4">
                Dynamic Peer Group Builder
              </Typography>
              <Divider style={{ marginBottom: ".5rem" }}></Divider>
              <Grid
                container
                spacing={1}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Grid item xs={10}>
                  <Button
                    className={classes.button}
                    disabled={!createDynamic}
                    onClick={() => setOpenSearchModal(true)}
                    id="peer-group-from-search"
                    sx={{ height: "auto" }}
                  >
                    Create Dynamic Peer Group
                  </Button>
                </Grid>
                <Grid item xs={2}>
                  <MoreInfoButton
                    hover
                    id="CreateDynamicPeerGroup"
                    popperPosition="left"
                    label="Dynamic Peer Group"
                    pgcPopup
                    pgcSideBar
                  >
                    <Typography style={{ paddingBottom: 0 }} variant="h4">
                      Dynamic Peer Group
                    </Typography>
                    <Typography style={{ color: "#1A4480" }}>
                      Useful for following Institutions that share similar
                      characteristics. The peer group is defined using the
                      search criteria of the results shown. This method finds
                      cohorts of similar banks based on their:
                      <List
                        className={classes.dynamic}
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                      >
                        <ListItem style={{ padding: 0 }}>
                          <ListItemIcon>
                            <FiberManualRecordIcon
                              className={classes.MuiSvgIcon}
                            />
                          </ListItemIcon>
                          <Typography style={{ color: "#1A4480" }}>
                            Financial Size
                          </Typography>
                        </ListItem>
                        <ListItem style={{ padding: 0 }}>
                          <ListItemIcon>
                            <FiberManualRecordIcon
                              className={classes.MuiSvgIcon}
                            />
                          </ListItemIcon>
                          <Typography style={{ color: "#1A4480" }}>
                            Banking Class or Regulator
                          </Typography>
                        </ListItem>
                        <ListItem style={{ padding: 0 }}>
                          <ListItemIcon>
                            <FiberManualRecordIcon
                              className={classes.MuiSvgIcon}
                            />
                          </ListItemIcon>
                          <Typography style={{ color: "#1A4480" }}>
                            Physical Location
                          </Typography>
                        </ListItem>
                        <ListItem style={{ padding: 0 }}>
                          <ListItemIcon>
                            <FiberManualRecordIcon
                              className={classes.MuiSvgIcon}
                            />
                          </ListItemIcon>
                          <Typography style={{ color: "#1A4480" }}>
                            Economic Specialties
                          </Typography>
                        </ListItem>
                        <ListItem style={{ padding: 0 }}>
                          <ListItemIcon>
                            <FiberManualRecordIcon
                              className={classes.MuiSvgIcon}
                            />
                          </ListItemIcon>
                          <Typography style={{ color: "#1A4480" }}>
                            and more...
                          </Typography>
                        </ListItem>
                      </List>
                    </Typography>
                    <Typography style={{ color: "#1A4480" }}>
                      This is a flexible and holistic view of the industry as
                      the peer group is dynamic and adjusts over time as
                      institutions come and go.
                    </Typography>
                    <Typography style={{ color: "#1A4480" }}>
                      <Typography component="span" variant="h4">
                        Note:{" "}
                      </Typography>
                      This button is only active when dynamic search criteria
                      has been used. Explicit criteria, such as “Cert” or
                      “Name”, negate the use of dynamic criteria.
                    </Typography>
                  </MoreInfoButton>
                </Grid>
              </Grid>

              <Typography
                style={{ paddingTop: 16, paddingBottom: 8 }}
                variant="h4"
              >
                Current Selections
              </Typography>
              <Divider></Divider>
              <Box
                sx={{
                  height: "132px",
                  width: "100%",
                  backgroundColor: "white",
                  border: "1px solid rgba(172, 200, 223, 1)",
                  marginTop: 2,
                  boxShadow: theme.shadows[5],
                  overflow: "scroll",
                }}
              >
                <div id="shopping-cart" style={{ margin: 5 }}>
                  {customCertSearchPills.props.dataArray.length > 0 ? (
                    <>{customCertSearchPills}</>
                  ) : (
                    <Typography
                      style={{
                        paddingTop: "24px",
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        textAlign: "center",
                        color: "#1A4480",
                        lineHeight: "1.2rem",
                      }}
                      variant="body1"
                    >
                      Use this area to add selections to your report as
                      individuals or combine them into a static peer group (see
                      below.)
                    </Typography>
                  )}{" "}
                </div>
              </Box>
              <Grid container mt={2}>
                <Grid item xs={5.5}>
                  <Button
                    className={classes.buttons}
                    disabled={selectSearchPills.length < 1}
                    onClick={() => addSelections(selectSearchPills)}
                    id="add-all-certs"
                  >
                    Add as Individual Certs
                  </Button>
                  <div
                    style={{
                      display: "grid",
                      justifyContent: "center",
                      marginTop: 8,
                    }}
                  >
                    <MoreInfoButton
                      hover
                      id="IndividualCerts"
                      popperPosition="left"
                      label="Add as Individual Certs"
                      pgcSideBar
                      pgcPopup
                    >
                      <Typography variant="h4" style={{ paddingBottom: 0 }}>
                        Add as Individual Certs
                      </Typography>
                      <Typography style={{ color: "#1A4480" }}>
                        This option uses the collected list of certs in the
                        Current Selections area. It will add them to your Report
                        Population as individual bank-to-bank comparisons for
                        these items.
                      </Typography>
                    </MoreInfoButton>
                  </div>
                </Grid>
                <Grid item xs={1}>
                  <Typography style={{ textAlign: "center", paddingTop: 12 }}>
                    or
                  </Typography>
                </Grid>
                <Grid item xs={5.5}>
                  <Button
                    className={classes.buttons}
                    disabled={selectSearchPills.length <= 1}
                    onClick={() => setOpenCertModal(true)}
                    id="create-peer-group-cert-list"
                  >
                    Create Static Peer Group
                  </Button>
                  <div
                    style={{
                      display: "grid",
                      justifyContent: "center",
                      marginTop: 8,
                    }}
                  >
                    <MoreInfoButton
                      hover
                      id="StaticPG"
                      popperPosition="left"
                      label="Static Peer Group from Cert List"
                      pgcSideBar
                      pgcPopup
                    >
                      <Typography variant="h4" style={{ paddingBottom: 0 }}>
                        Static Peer Group from Cert List
                      </Typography>
                      <Typography style={{ color: "#1A4480" }}>
                        This option uses the collected list of certs in the
                        Current Selections area to build a static peer group.
                      </Typography>
                      <Typography style={{ color: "#1A4480" }}>
                        This method is useful for aggregating specific
                        institutions together. It is limited to only the
                        institutions listed and will not grow (gain
                        institutions) over time.
                      </Typography>
                      <Typography style={{ color: "#1A4480" }}>
                        This method can shrink over time as institutions cease
                        to operate (failure/merger) or if a reporting period is
                        selected before an institution’s existence.
                      </Typography>
                    </MoreInfoButton>
                  </div>
                </Grid>
              </Grid>
            </Box>
          </div>
        ) : (
          <Button
            id="add-all-btn"
            fullWidth
            color="secondary"
            variant="contained"
            style={{
              marginTop:
                appState.ui.savedPGCSearch &&
                (peerGroupSelections || standardPeerGroupCustomCompare) &&
                appState.ui.searchPush
                  ? 0
                  : 80,
            }}
            disabled={
              (appState.ui.savedGroupId === "savedPeerGroups" &&
                (!subOptionsSelected ||
                  (subOptionsSelected &&
                    !Object.values(appState.ui.subOptionsSelected).some(
                      val => val === true,
                    )))) ||
              !appState.ui.savedGroupId
            }
            className={classes.addSelection}
            onClick={() => addAllPeerGroupsToReport()}
          >
            Add All To Report Population
          </Button>
        )}
      </Grid>
    </Grid>
  );
  return (
    <>
      {!appState.ui.createGroupType && reportPopulationContent}
      {hasErrors && displayErrorMessage && (
        <Alert
          id="errors-exist"
          severity="error"
          style={{ padding: "0 .5rem", marginBottom: "1rem" }}
        >
          Errors detected with Peer Group. Please correct before continuing.
        </Alert>
      )}

      {peerGroupCertModal}
      {peerGroupSearchModal}
      {previewGroupModal}
      {buildSearch && modalInputField}
      {buildSearch && errors.noSearch && displayErrorMessage && noCheckbox && (
        <Alert
          severity="error"
          id="search-required"
          style={{ padding: "0 .5rem", marginBottom: "1rem" }}
        >
          Search criteria is required when creating a Dynamic Peer Group.
        </Alert>
      )}
      {appState.ui.createGroupType && tableDisplay}
    </>
  );
}
